import { ArrowBack } from "@mui/icons-material";
import { Box, Button, CircularProgress, IconButton, Modal } from "@mui/material";
import { useState } from "react";
import { IMovimentacaoResumed, IFetchWorkspaceTemplate, IBeneficiaryRetificar, ViewMode } from "../../types";
import { RetificarTable } from "./Table";
import { ModalRetificar } from "./ModalRetificar";
import { useAlert } from "contexts/alert";
import api from "services/api";
import { useParams } from "react-router-dom";

interface IRetificarViewProps {
  movimentacao: IMovimentacaoResumed;
  template: IFetchWorkspaceTemplate;
  setViewMode: (view: ViewMode) => void;
}

export function RetificarView({ movimentacao, template, setViewMode }: IRetificarViewProps) {
  const { workspaceId, templateId } = useParams() as { workspaceId: string, templateId: string };
  const { openAlert } = useAlert();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSendingApproval, setIsSendingApproval] = useState(false);
  const [isRetificarModalOpen, setIsRetificarModalOpen] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<IBeneficiaryRetificar | null>(null);

  // D9 (Onde a Plataforma Mente, 18/08/2026): "status" e "documentos" não existem em
  // IMovimentacaoResumed.result — vinham fixos em "Aprovado" e sorteados com Math.random().
  // Sem uma fonte real para os dois, a tabela deixou de exibi-los (ver Table.tsx) em vez
  // de continuar mostrando um dado inventado.
  const beneficiaries: IBeneficiaryRetificar[] = movimentacao.result.map(item => ({
    nome: item.nome,
    cpfBeneficiario: item.cpfDependente || item.cpf,
    cpfTitular: item.cpf,
    tipo: item.parentesco,
    dataDemissao: item.dataDemissao || "-",
    plano: item.plano,
  }));

  async function handleApproval() {
    try {
      setIsSendingApproval(true);
      const dagRunId = movimentacao.result.find(m => m.dagRunId)?.dagRunId ?? null;
      const approveActivity = template.activities.find(activity => activity.name === "Aprovação");

      await api.post(`/v2/workspaces/${workspaceId}/templates/${templateId}/approval/${approveActivity?.workspaceActivityId}`, {
        tableName: "MASSA_MOVIMENTADA",
        dagRunId: dagRunId,
      });

      openAlert('Movimentações aprovadas com sucesso', 'success');
      setViewMode("RESUMED");
    } catch (error) {
      openAlert('Houve algum erro na aprovação', 'error');
    } finally {
      setIsSendingApproval(false);
      setIsModalOpen(false);
    }
  }

  function handleRetificarClick(beneficiary: IBeneficiaryRetificar) {
    setSelectedBeneficiary(beneficiary);
    setIsRetificarModalOpen(true);
  }

  function handleRetificarSubmit() {
    // D9: não existe endpoint de retificação no backend. O ModalRetificar mantém o botão
    // de envio desabilitado por isso mesmo, então este caminho não deveria ser alcançável
    // pela UI; o aviso abaixo é a rede de segurança caso ele seja chamado de outro lugar.
    openAlert('Retificação por aqui ainda não está disponível. Fale com o time Conducco.', 'info');
    setIsRetificarModalOpen(false);
    setSelectedBeneficiary(null);
  }

  return (
    <>
      <Modal
        open={isModalOpen}
        aria-labelledby="modal-approval-title"
        aria-describedby="modal-approval-description"
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            height: "auto",
            width: "100%",
            maxWidth: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: "1rem",
            border: "none",
            outline: "none",
            padding: "24px",
          }}
        >
          <h6 style={{ padding: "0", margin: "0", fontSize: "1.25rem", lineHeight: "1.75rem", fontWeight: 600, marginBottom: "24px" }}>
            Aprovar movimentações
          </h6>
          <p style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A', margin: "0 0 34px 0" }}>
            Você confirma que revisou todos os dados e deseja aprovar as movimentações?
          </p>

          <div style={{ display: "flex", paddingTop: "0.5rem", paddingBottom: "0.5rem", marginTop: "1rem", justifyContent: "flex-end", alignItems: "center", width: "100%", gap: "0.5rem" }}>
            <Button
              variant='outlined'
              color='error'
              style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
              onClick={() => setIsModalOpen(false)}
              disabled={isSendingApproval}
            >
              Cancelar
            </Button>

            <Button
              variant='contained'
              style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600, minWidth: "120px" }}
              onClick={handleApproval}
              disabled={isSendingApproval}
            >
              {!isSendingApproval ? "Aprovar" : <CircularProgress size="20px" />}
            </Button>
          </div>
        </Box>
      </Modal>

      <div style={{ width: "100%", padding: "24px" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IconButton onClick={() => setViewMode("RESUMED")}>
              <ArrowBack />
            </IconButton>
            <h1 style={{ padding: "0", margin: "0", fontSize: "20px", lineHeight: "32px", letterSpacing: "0.15px", fontWeight: 600, fontFamily: "Sora, sans-serif" }}>
              Retificar
            </h1>
          </div>
        </div>

        <RetificarTable data={beneficiaries} onRetificarClick={handleRetificarClick} />

        <ModalRetificar
          isOpen={isRetificarModalOpen}
          onClose={() => setIsRetificarModalOpen(false)}
          beneficiary={selectedBeneficiary}
          onSubmit={handleRetificarSubmit}
        />

        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: "1.5rem", marginTop: "150px" }}>
          <p style={{ fontSize: '16px', lineHeight: '24px', letterSpacing: '0.15px', color: '#6F6F78', margin: "0", fontFamily: "Nunito Sans, sans-serif" }}>
            Aprove as movimentações clicando no botão ao lado
          </p>
          <Button
            variant='outlined'
            style={{ padding: "8px 22px", borderRadius: "8px", fontSize: "15px", lineHeight: "26px", letterSpacing: "0.46px", fontWeight: 600, color: "#3024e1", borderColor: "rgba(48, 36, 225, 0.5)" }}
            onClick={() => setIsModalOpen(true)}
          >
            Aprovar movimentações
          </Button>
        </div>
      </div>
    </>
  );
}
